import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect } from "react";
import { useUUID } from "../../../hooks/useUUID.js";
import { useFieldContext } from "./FieldContext.js";
import { InternalFlexBox } from "../../flexBox/internal/InternalFlexBox.js";
import { IconSuccess } from "../../../icons/IconSuccess.js";
import { IconAlert } from "../../../icons/IconAlert.js";
import { IconDanger } from "../../../icons/IconDanger.js";
import { Text } from "../../text/Text.js";
import { combineClasses } from "../../../utilities/combineClasses.js";
import BaseMessage_module from "./BaseMessage.module.js";
import { VisuallyHidden, visuallyHiddenStyles } from "../../visuallyHidden/VisuallyHidden.js";
import { useFormContext } from "../FormContext.js";
const BaseMessage = /*#__PURE__*/ forwardRef(({ children, status, id, className, testId, disabled, ...props }, ref)=>{
    const uuid = useUUID({
        prefix: 'message'
    });
    const fieldContext = useFieldContext();
    const addDescriptionId = fieldContext?.addDescriptionId;
    const removeDescriptionId = fieldContext?.removeDescriptionId;
    const addErrorMessageId = fieldContext?.addErrorMessageId;
    const removeErrorMessageId = fieldContext?.removeErrorMessageId;
    const { Icon, color } = status ? statusToIcon[status] : {
        Icon: void 0,
        color: void 0
    };
    useEffect(()=>{
        if (void 0 === id) {
            const isErrorMessage = 'error' === status;
            const addId = isErrorMessage ? addErrorMessageId : addDescriptionId;
            const removeId = isErrorMessage ? removeErrorMessageId : removeDescriptionId;
            addId?.(uuid);
            return ()=>removeId?.(uuid);
        }
    }, [
        id,
        status,
        uuid,
        addErrorMessageId,
        addDescriptionId,
        removeErrorMessageId,
        removeDescriptionId
    ]);
    const hasMeaningfulChildren = children && '' !== children;
    return /*#__PURE__*/ jsx(InternalFlexBox, {
        ref: ref,
        gap: "100",
        id: id ?? uuid,
        "aria-live": 'error' === status ? 'polite' : void 0,
        "aria-atomic": 'error' === status ? true : void 0,
        "data-input-type": fieldContext?.inputType,
        "data-size": fieldContext?.size,
        className: combineClasses(className, BaseMessage_module.baseMessage, !hasMeaningfulChildren && visuallyHiddenStyles.visuallyHidden),
        ...props,
        children: hasMeaningfulChildren && /*#__PURE__*/ jsxs(Fragment, {
            children: [
                Icon && /*#__PURE__*/ jsx(Icon, {
                    "aria-hidden": true,
                    size: "small",
                    color: color
                }),
                'error' === status && /*#__PURE__*/ jsx(VisuallyHiddenInputLabel, {}),
                /*#__PURE__*/ jsx(Text, {
                    color: status ? statusToColor[status] : disabled ? 'gray-500' : 'gray-700',
                    size: "100",
                    testId: testId,
                    children: children
                })
            ]
        })
    });
});
BaseMessage.displayName = 'BaseMessage';
const statusToColor = {
    success: 'success-700',
    warning: 'alert-700',
    error: 'danger-700'
};
const statusToIcon = {
    success: {
        Icon: IconSuccess,
        color: 'success-600'
    },
    warning: {
        Icon: IconAlert,
        color: 'alert-500'
    },
    error: {
        Icon: IconDanger,
        color: 'danger-600'
    }
};
const VisuallyHiddenInputLabel = ()=>{
    const formContext = useFormContext();
    const fieldContext = useFieldContext();
    const labelText = (fieldContext?.labelId ? document.querySelector(`#${fieldContext.labelId}`)?.textContent ?? '' : '').replace(/\*$/, '');
    return /*#__PURE__*/ jsxs(VisuallyHidden, {
        children: [
            labelText,
            ' ',
            (formContext?.submitCounter ?? 0) % 2 === 0 ? null : /*#__PURE__*/ jsx("pre", {
                children: " "
            })
        ]
    });
};
export { BaseMessage };
